import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { PATHS } from '../routes'
import { ReplaySubject, firstValueFrom, tap } from 'rxjs'
import { AuthenticatedCacheService } from '@services/authenticated-cache.service'
import { TokenRepositoryService } from './token-repository.service'
import { WindowProvider } from '../helpers/providers/window.provider'
import { environment } from '@environments/environment'

export interface TokenDTO {
  expires_in: number
  access_token: string
  refresh_token?: string
  token_type: string
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {

    private loggedIn  = new ReplaySubject<boolean>(1)
    loggedIn$ = this.loggedIn.asObservable()
    private refreshTokenPromise: Promise<TokenDTO> | null = null

    constructor(
        private http: HttpClient,
        private tokenRepository: TokenRepositoryService,
        private windowProvider: WindowProvider,
        private authenticatedCacheService: AuthenticatedCacheService
    ) { }

    checkLoggedInStatus() {
        const loggedIn = this.getToken() !== null
        this.loggedIn.next(loggedIn)
    }

    getToken(): string | null {
        return this.tokenRepository.getAccessToken()
    }

    logout() {
        this.authenticatedCacheService.clearAll()
        this.tokenRepository.removeTokens()
        this.windowProvider.navigateToUrl(this.getCognitoUrl('logout'))
    }

    login() {
        this.windowProvider.navigateToUrl(this.getCognitoUrl('login'))
    }

    reauthorise() {
        this.windowProvider.navigateToUrl(this.getCognitoUrl('oauth2/authorize'))
    }

    async fetchToken(code: string): Promise<TokenDTO> {
        const url = `${environment.cognitoAuthUrl}/oauth2/token`
        const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
        }
        const options = {
            headers: headers,
        }
        const body = new HttpParams()
            .set('client_id', environment.cognitoClientId)
            .set('grant_type', 'authorization_code')
            .set('code', code)
            .set('redirect_uri', this.getAuthCallbackUri())

        return firstValueFrom(
            this.http.post<TokenDTO>(url, body, options)
                .pipe(
                    tap((tokenData) => {
                        this.setToken(tokenData.access_token, tokenData.refresh_token)
                    })
                )
        )
    }

    async refreshToken(): Promise<TokenDTO> {
        if (this.refreshTokenPromise !== null) {
            return this.refreshTokenPromise
        }
        const refreshToken = this.tokenRepository.getRefreshToken()
        if (!refreshToken) {
            throw new Error('No refresh token')
        }

        const url = `${environment.cognitoAuthUrl}/oauth2/token`
        const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
        }
        const options = {
            headers: headers,
        }

        const body = new HttpParams()
            .set('client_id', environment.cognitoClientId)
            .set('grant_type', 'refresh_token')
            .set('refresh_token', refreshToken)
            .set('scope', environment.cognitoScopes)

        this.refreshTokenPromise = firstValueFrom(
            this.http.post<TokenDTO>(url, body, options)
                .pipe(
                    tap((tokenData) => {
                        this.setToken(tokenData.access_token, tokenData.refresh_token)
                    })
                )
        ).finally(() => {
            this.refreshTokenPromise = null
        })
        return this.refreshTokenPromise
    }

    saveSignupTokens(accessToken: string, refreshToken: string) {
        this.setToken(accessToken, refreshToken)
    }

    private getAuthCallbackUri() {
        return `${this.windowProvider.getLocationOrigin()}/${PATHS.authCallback}`
    }

    private getCognitoUrl(path: string) {
        const params = new HttpParams()
            .set('client_id', environment.cognitoClientId)
            .set('response_type', 'code')
            .set('state', 'STATE')
            .set('scope', environment.cognitoScopes)
            .set('redirect_uri', this.getAuthCallbackUri())

        return `${environment.cognitoAuthUrl}/${path}?${params.toString()}`
    }

    private setToken(accessToken: string, refreshToken?: string) {
        const saveTokens = () => {
            this.tokenRepository.saveAccessToken(accessToken)
            if (refreshToken !== undefined) {
                this.tokenRepository.saveRefreshToken(refreshToken)
            }
        }
        try {
            saveTokens()
        } catch (error) {
            if (!(error instanceof DOMException) || error.name !== 'QuotaExceededError') {
                throw error
            }
            this.authenticatedCacheService.clearAll()
            saveTokens()
        }
        this.loggedIn.next(true)
    }
}
